import api from './api';

class AuthService {
    // Login user
    async login(email, password, rememberMe = false) {
        try {
            // First, fetch CSRF token
            await api.get('/users/auth/csrf/');

            const response = await api.post('/users/auth/login/', {
                email,
                password,
                remember_me: rememberMe
            });

            const { access, refresh, user } = response.data;

            // Store tokens
            localStorage.setItem('access_token', access);
            localStorage.setItem('refresh_token', refresh);
            localStorage.setItem('user', JSON.stringify(user));

            return { success: true, user, access, refresh };
        } catch (error) {
            // Handle email verification required
            if (error.response?.status === 403 && error.response?.data?.error === 'email_verification_required') {
                return {
                    success: false,
                    requiresVerification: true,
                    email: error.response.data.email,
                    message: error.response.data.detail
                };
            }

            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Login failed'
            };
        }
    }

    // Register new user
    async register(userData) {
        try {
            const response = await api.post('/users/users/register/', userData);
            return response.data;
        } catch (error) {
            // Handle different error types
            if (error.response?.status === 400) {
                // Validation errors from Django REST Framework
                const errorData = error.response.data;

                // If it's a simple detail/message error
                if (errorData?.detail || errorData?.message) {
                    throw new Error(errorData.detail || errorData.message);
                }

                // If it's field-specific validation errors (DRF format)
                if (typeof errorData === 'object' && errorData) {
                    const fieldErrors = [];
                    for (const [field, messages] of Object.entries(errorData)) {
                        if (Array.isArray(messages)) {
                            fieldErrors.push(`${field}: ${messages.join(', ')}`);
                        } else {
                            fieldErrors.push(`${field}: ${messages}`);
                        }
                    }
                    if (fieldErrors.length > 0) {
                        throw new Error(fieldErrors.join('\n'));
                    }
                }

                // Fallback for other 400 errors
                throw new Error('Invalid registration data');
            } else if (error.response?.status === 409) {
                // Conflict (user already exists)
                throw new Error('An account with this email already exists');
            } else if (error.response?.status >= 500) {
                // Server errors
                throw new Error('Server error. Please try again later.');
            } else {
                // Other errors
                throw new Error(error.response?.data?.detail || error.message || 'Registration failed');
            }
        }
    }

    // Logout user
    async logout() {
        try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
                await api.post('/users/auth/logout/', {
                    refresh_token: refreshToken
                });
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Clear tokens regardless of API call success
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
        }
    }

    // Get current user
    getCurrentUser() {
        const userStr = localStorage.getItem('user');
        return userStr ? JSON.parse(userStr) : null;
    }

    // Check if user is authenticated
    isAuthenticated() {
        const token = localStorage.getItem('access_token');
        return !!token;
    }

    // Get access token
    getAccessToken() {
        return localStorage.getItem('access_token');
    }

    // Get refresh token
    getRefreshToken() {
        return localStorage.getItem('refresh_token');
    }

    // Refresh access token
    async refreshToken() {
        try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (!refreshToken) {
                throw new Error('No refresh token available');
            }

            const response = await api.post('/users/auth/refresh/', {
                refresh: refreshToken
            });

            const { access, refresh } = response.data;
            localStorage.setItem('access_token', access);

            // Handle token rotation - store new refresh token if provided
            if (refresh) {
                localStorage.setItem('refresh_token', refresh);
            }

            return access;
        } catch (error) {
            // If refresh fails, logout user
            this.logout();
            throw error;
        }
    }

    // Get user profile
    async getProfile() {
        try {
            const response = await api.get('/users/profile/');
            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to fetch profile'
            };
        }
    }

    // Update user profile
    async updateProfile(userData) {
        try {
            const response = await api.patch('/users/profile/', userData);

            // Update stored user data
            localStorage.setItem('user', JSON.stringify(response.data));

            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to update profile'
            };
        }
    }

    // Verify OTP for email verification
    async verifyOTP(email, code) {
        try {
            const response = await api.post('/users/auth/verify-otp/', {
                email,
                code
            });

            // If verification successful and tokens provided, store them
            if (response.data.success && response.data.token) {
                const { access, refresh } = response.data.token;
                localStorage.setItem('access_token', access);
                localStorage.setItem('refresh_token', refresh);
                localStorage.setItem('user', JSON.stringify(response.data.user));
            }

            return response.data;
        } catch (error) {
            throw error;
        }
    }

    // Resend verification email
    async resendVerification(email) {
        try {
            const response = await api.post('/users/auth/resend-verification/', {
                email
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    // Alias for consistency
    async resendVerificationEmail(email) {
        return this.resendVerification(email);
    }

    // Validate current token
    async validateToken() {
        try {
            const response = await api.get('/users/auth/validate/');
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    // Change password
    async changePassword(currentPassword, newPassword) {
        // TODO: Implement change password endpoint in Django
        throw new Error('Change password functionality not yet implemented');
    }

    // Request password reset
    async requestPasswordReset(email) {
        // TODO: Implement password reset endpoint in Django
        throw new Error('Password reset functionality not yet implemented');
    }

    // Confirm password reset
    async confirmPasswordReset(token, newPassword) {
        // TODO: Implement password reset confirmation endpoint in Django
        throw new Error('Password reset confirmation functionality not yet implemented');
    }
}

export default new AuthService(); 